import { useEffect, useRef, useState } from "react";

function useRevealOnScroll(ref) {
  useEffect(() => {
    const elements = ref.current?.querySelectorAll("[data-reveal]");
    if (!elements) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay || "0";
            setTimeout(() => {
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, parseFloat(delay));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 },
    );

    elements.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(20px)";
      el.style.transition = "opacity 0.75s ease, transform 0.75s ease";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

// ─── Replace with your real YouTube video IDs ────────────────────────────
// YouTube video ID is the part after ?v= in the URL
// e.g. https://www.youtube.com/watch?v=XXXXXXXXXXX → "XXXXXXXXXXX"
const featuredVideo = {
  id: "kPDFkuvz3jg",
  title: "Sound Journey — Live Ceremony",
  description:
    "A full sound healing session recorded live during ceremony. Oud, handpan and voice.",
};

const gridVideos = [
  {
    id: "VmrncWjGKr8",
    title: "Ayahuasca ceremony music",
  },
  {
    id: "pFHijH1J-c4",
    title: "Morning sound meditation",
  },
  {
    id: "Tu96p5U9c1w",
    title: "Men's circle — closing song",
  },
];

// ─── Replace with your Spotify embed URL ────────────────────────────────
// From Spotify: Share → Embed → copy the src URL from the iframe code
// Looks like: https://open.spotify.com/embed/playlist/XXXXX?utm_source=...

const spotifyEmbedUrl =
  "https://open.spotify.com/embed/artist/5p3IIXsYQPHxCrtggdRAJ6?utm_source=generator&theme=0";
// ────────────────────────────────────────────────────────────────────────

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 ml-1">
      <path d="M8 5.14v14l11-7-11-7z" />
    </svg>
  );
}

function YouTubeThumbnail({ videoId, title, featured = false }) {
  const [playing, setPlaying] = useState(false);
  const thumbUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const sdThumbUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (playing) {
    return (
      <div
        className={`relative w-full ${featured ? "aspect-video" : "aspect-video"} bg-ink`}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    );
  }

  return (
    <button
      onClick={() => setPlaying(true)}
      className="relative w-full aspect-video bg-ink/80 group block overflow-hidden"
      aria-label={`Play: ${title}`}
    >
      {/* Thumbnail */}
      <img
        src={thumbUrl}
        onError={(e) => {
          e.target.src = sdThumbUrl;
        }}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
      />

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />

      {/* Play button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-full border border-cream/40 flex items-center justify-center text-cream/80 group-hover:border-earth-light group-hover:text-earth-light group-hover:scale-110 transition-all duration-300 bg-ink/30 backdrop-blur-sm">
          <PlayIcon />
        </div>
      </div>

      {/* Title on featured */}
      {featured && (
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <p className="font-jost font-light text-cream/50 text-[0.65rem] tracking-[0.2em] uppercase mb-1">
            Featured
          </p>
          <p className="font-cormorant italic font-light text-cream text-xl leading-tight">
            {title}
          </p>
        </div>
      )}

      {/* Title on grid items */}
      {!featured && (
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="font-cormorant italic font-light text-cream/80 text-base leading-tight text-left">
            {title}
          </p>
        </div>
      )}
    </button>
  );
}

export const MusicContent = () => {
  const sectionRef = useRef(null);
  useRevealOnScroll(sectionRef);

  return (
    <section
      id="music"
      ref={sectionRef}
      className="bg-ink text-cream py-24 lg:py-32 px-8 lg:px-16 relative overflow-hidden"
    >
      {/* Subtle texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            #C4A274 0px, #C4A274 1px,
            transparent 1px, transparent 14px
          )`,
        }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto relative">
        {/* ── Section header ── */}
        <div className="mb-16 lg:mb-20">
          <div className="flex items-baseline gap-6 flex-wrap">
            <p
              data-reveal
              data-delay="0"
              className="text-[0.65rem] tracking-[0.3em] uppercase text-earth-light/60 font-jost font-light whitespace-nowrap"
            >
              Sound & music
            </p>
            <h2
              data-reveal
              data-delay="80"
              className="font-cormorant font-light text-cream leading-[1.1]"
              style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.5rem)" }}
            >
              Music made for{" "}
              <em className="italic text-earth-light">going inward</em>
            </h2>
          </div>

          <p
            data-reveal
            data-delay="160"
            className="mt-6 font-jost font-light text-cream/55 leading-[1.85] max-w-[520px]"
            style={{ fontSize: "clamp(0.9rem, 1.05vw, 1rem)" }}
          >
            Every piece of music I play is made for the space between — between
            waking and dreaming, between who you were and who you're becoming.
            Listen before you book. Let the music tell you whether this is
            right.
          </p>
        </div>

        {/* ── Featured video ── */}
        <div data-reveal data-delay="200" className="mb-6">
          <YouTubeThumbnail
            videoId={featuredVideo.id}
            title={featuredVideo.title}
            featured
          />
          <p className="mt-4 font-jost font-light text-cream/40 text-sm leading-relaxed max-w-xl">
            {featuredVideo.description}
          </p>
        </div>

        {/* ── Video grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-16">
          {gridVideos.map((v, i) => (
            <div key={v.id} data-reveal data-delay={`${300 + i * 80}`}>
              <YouTubeThumbnail videoId={v.id} title={v.title} />
            </div>
          ))}
        </div>

        {/* ── Divider ── */}
        <div
          data-reveal
          data-delay="400"
          className="flex items-center gap-6 mb-16"
        >
          <div className="flex-1 h-px bg-white/8" />
          <p className="font-jost font-light text-cream/30 text-[0.65rem] tracking-[0.3em] uppercase whitespace-nowrap">
            Also on Spotify
          </p>
          <div className="flex-1 h-px bg-white/8" />
        </div>

        {/* ── Spotify embed ── */}
        <div data-reveal data-delay="480">
          <iframe
            src={spotifyEmbedUrl}
            width="100%"
            height="352"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title="Noah Fernandes on Spotify"
            className="rounded-none border-0"
            style={{ borderRadius: 0 }}
          />
          <p className="mt-4 font-jost font-light text-cream/30 text-[0.72rem] tracking-wide">
            Follow along on Spotify for new recordings from ceremony and studio.
          </p>
        </div>
      </div>
    </section>
  );
};
